import React, { useState, useEffect } from "react";
import { Grid, Box, Button, Typography, CircularProgress, TextField, FormControlLabel, Checkbox } from "@mui/material";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import { useCompany } from "../../contexts/CompanyContext";
import { createDevolution, getDevolutionById, updateDevolution } from "../../api/devolutions/index.ts";
import { useSnack } from "../../services/SnakbarProvider.js";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import { getProductList } from "../../api/products";
import { getWarehouseList } from "../../api/warehouses/index.ts"

interface DevolutionFormProps {
  onSubmit: () => void;
  id?: number;
}

const validationSchema = Yup.object().shape({
  companyId: Yup.number().required("La empresa es requerida"),
  productId: Yup.number().required("El producto es requerido"),
  quantity: Yup.number()
    .required("La cantidad es requerida")
    .min(0.01, "La cantidad debe ser mayor que 0"),
  devolutionReason: Yup.string().required("La razón de devolución es requerida"),
  isWaste: Yup.boolean(),
  warehouseId: Yup.number().nullable(),
});

const DevolutionForm: React.FC<DevolutionFormProps> = ({ onSubmit, id }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<any>({
    companyId: null,
    productId: null,
    quantity: 0,
    devolutionReason: "",
    isWaste: false,
    warehouseId: null,
  });
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getDevolutionById(id);
      const data = response.data;
      setFormData({
        companyId: data.companyId,
        productId: data.productId,
        quantity: data.quantity,
        devolutionReason: data.devolutionReason || "",
        isWaste: data.isWaste || false,
        warehouseId: data.warehouseId || null,
      });
    } catch (error) {
      console.log(error);
      openSnack("Error al cargar la devolución", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formMode === "edit" && id) {
      fetchData();
    } else {
      setFormData({
        companyId: selectedCompany.id,
        productId: null,
        quantity: 0,
        devolutionReason: "",
        isWaste: false,
        warehouseId: null,
      });
      setLoading(false);
    }
  }, [id, selectedCompany.id, formMode]);

  const createPayload = (values: any) => {
    return {
      ...values,
      companyId: selectedCompany.id,
    };
  };

  const handleSubmit = async (values: any) => {
    const payload = createPayload(values);
    try {
      if (id) {
        await updateDevolution(id, payload);
        openSnack("Devolución actualizada correctamente", "success");
      } else {
        await createDevolution(payload);
        openSnack("Devolución creada correctamente", "success");
      }
      onSubmit();
    } catch (error) {
      openSnack("Error al guardar la devolución", "error");
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, errors }) => (
        <Form>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Devolución de Productos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FastField
                  name="warehouseId"
                  as={DropdownSelector}
                  label="Almacén"
                  fetchFunction={getWarehouseList}
                  optionLabel={["name"]}
                  value={values.warehouseId}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FastField
                  name="productId"
                  as={DropdownSelector}
                  label="Producto"
                  fetchFunction={getProductList}
                  fetchParams={{ companyId: selectedCompany.id }}
                  optionLabel={["code", "description"]}
                  value={values.productId}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FastField
                  name="quantity"
                  as={TextField}
                  label="Cantidad"
                  type="number"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={values.quantity}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12}>
                <FastField
                  name="devolutionReason"
                  as={TextField}
                  label="Razón de Devolución"
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={values.devolutionReason}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <FastField
                      name="isWaste"
                      as={Checkbox}
                      checked={values.isWaste}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("isWaste", e.target.checked);
                      }}
                    />
                  }
                  label="Es desperdicio"
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    {formMode === "edit" ? "Actualizar" : "Guardar"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default DevolutionForm; 